import { createSelector } from "@reduxjs/toolkit";
import * as React from "react";
import { useSelector } from "react-redux";

import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { BulletList } from "@com.mgmtp.a12.widgets/widgets-core";
import { addPrefix, joinClassNames } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { BaseColumnType, Table } from "@com.mgmtp.a12.widgets/widgets-core/lib/table/new-api";

import { SimpleContentBox } from "../../component/SimpleContentBox";
import { RESOURCE_KEYS } from "../../localization";
import { isUser, selectStructuredRoles } from "../../utilities/user";

import { getFVManifests } from "../slices/fachverfahren/selectors";
import { Manifest } from "../slices/manifest/types";
import { getSDManifests } from "../slices/stammdaten/selectors";

const COMPONENT_RESOURCE_KEYS = RESOURCE_KEYS.application.view.roles;
type RoleKey = keyof typeof RESOURCE_KEYS.application.view.roles.role;
const roleSignificanceList = ["leiter", "sachbearbeiter_erweitert", "sachbearbeiter", "betrachter"];

interface TableRow {
	readonly type: "FV" | "SD";
	readonly name: string;
	readonly client?: string;
	readonly roles: string[];
}

export const RolesPage: React.ComponentType<View> = function RolesPage(props) {
	const { localizer } = React.useContext(LocalizerContext);

	const data = useSelector(selectTableData);

	const columns = React.useMemo<BaseColumnType<TableRow>[]>(
		() => [
			{
				hiddenText: localizer({ key: COMPONENT_RESOURCE_KEYS.column.type.title }),
				label: <Icon iconTheme="outlined">info</Icon>,
				dataKey: "type",
				fixedWidth: true,
				width: 0.6
			},
			{
				label: localizer({ key: COMPONENT_RESOURCE_KEYS.column.name.label }),
				dataKey: "name",
				width: 1.5
			},
			...(data.some(it => it.client)
				? [
						{
							label: localizer({ key: COMPONENT_RESOURCE_KEYS.column.client.label }),
							dataKey: "client"
						}
					]
				: []),
			{
				label: localizer({ key: COMPONENT_RESOURCE_KEYS.column.baseRole.label }),
				dataGetter: ({ row: { roles } }) => (
					<BulletList.Unordered indent={false} className={joinClassNames(addPrefix("-u-margin-0"))}>
						{roles.map(role => (
							<BulletList.Item key={role}>{role}</BulletList.Item>
						))}
					</BulletList.Unordered>
				)
			}
		],
		[localizer, data]
	);

	return (
		<SimpleContentBox
			activityId={props.activityId}
			title={localizer({ key: COMPONENT_RESOURCE_KEYS.title })}
			closeButtonTitle={localizer({ key: COMPONENT_RESOURCE_KEYS.buttons.close.title })}
			ariaLevel={props.ariaLevel ?? 1}
		>
			<Table<TableRow> data={data} columns={columns} />
		</SimpleContentBox>
	);
};

const selectTableData = createSelector(
	[UaaSelectors.user, getFVManifests, getSDManifests],
	(user, fvManifests, sdManifests) =>
		isUser(user)
			? selectStructuredRoles(user)
					.flatMap<TableRow>(({ key, client, roles }) => {
						let manifest: Manifest | undefined = fvManifests.find(it => it.identification.key === key);
						let type: "FV" | "SD" = "FV";

						if (!manifest) {
							manifest = sdManifests.find(it => it.identification.key === key);
							type = "SD";
						}

						if (!manifest) {
							return [];
						}

						const { localizer } = React.useContext(LocalizerContext);
						const displayRoles = sortRoles(roles).map(role => {
							const displayRole: string | undefined = roleSignificanceList.includes(role)
								? localizer({ key: RESOURCE_KEYS.application.view.roles.role[role as RoleKey] })
								: manifest?.roles?.find(r => r?.role?.toLowerCase() === role?.toLowerCase())
										?.description;
							return displayRole ?? role;
						});

						const name = manifest.identification.shortName;
						return { type, name, client, roles: displayRoles };
					})
					.sort(createComparator("type", "name", "client"))
			: []
);

function sortRoles(roles: string[]): string[] {
	const priority = new Map(roleSignificanceList.map((role, index) => [role, index]));

	return [...roles].sort((a, b) => {
		const aPriority = priority.has(a) ? priority.get(a)! : Number.MAX_SAFE_INTEGER;

		const bPriority = priority.has(b) ? priority.get(b)! : Number.MAX_SAFE_INTEGER;

		return aPriority - bPriority;
	});
}

function createComparator<T extends object>(
	key: keyof T,
	keyNext?: keyof T,
	...rest: (keyof T)[]
): (a: T, b: T) => number {
	const next = keyNext ? createComparator(keyNext, ...rest) : () => 0;
	return (a, b) => {
		if (!key) {
			return 0;
		}

		const propertyA = `${a[key] ?? ""}`;
		const propertyB = `${b[key] ?? ""}`;

		return propertyA < propertyB ? -1 : propertyA > propertyB ? 1 : next(a, b);
	};
}
